<!-- @migration-task Error while migrating Svelte code: `<form>` cannot be a descendant of `<form>`. The browser will 'repair' the HTML (by moving, removing, or inserting elements) which breaks Svelte's assumptions about the structure of your components.
https://svelte.dev/e/node_invalid_placement -->
<script lang="ts">
    import { onMount } from "svelte";
    import { ConfigurationManager } from "../../ConfigurationManager";
    import {
        type IBookingController,
        type IGamesController,
    } from "../../controller";
    import { Slot, SLOT_TYPES } from "../../model/Slot";
    import BootstrapVectorIcon from "../components/BootstrapVectorIcon.svelte";
    import {
        Booking,
        BOOKING_STATUS,
        BookingAsset,
    } from "../../model/Booking";

    import {
        DefaultErrorHandler,
        getMessagingService,
        MESSAGE_TYPE,
        type MessagingService,
    } from "../../utility/ContextHelper.svelte";

    import * as bip39 from "@scure/bip39";
    import { wordlist } from "@scure/bip39/wordlists/english";

    import BookingRequestSummary from "../fragments/BookingRequestSummary.svelte";
    import SlotAssetStep from "../fragments/SlotAssetStep.svelte";
    import { AuthInfo, getAuthInfo } from "../../utility/AccountUtility";
    import { capitalizeFirstLetter } from "../../utility/helper";

    export let params = { id: undefined };

    let gamesController: IGamesController =
        ConfigurationManager.getInstance().gamesController;
    let campaginController: IBookingController =
        ConfigurationManager.getInstance().bookingController;

    let msgService: MessagingService = getMessagingService();

    /**
     * The currently active wizard page (1-based)
     */
    let activePage: number = 1;

    /**
     * The slot for which we are creating an asset
     */
    let slot: Slot = undefined;

    let bookingRequest: Booking = new Booking({
        id: undefined,
        name: "",
        slotId: "",
        advertiser: "",
        brand: "",
        volumeTotal: 0,
        status: BOOKING_STATUS.draft,
        assets: [],
    });

    /**
     * The estimated number of impressions based on the budget
     */
    let estimatedNumberOfImpressions: number = undefined;

    /**
     * The estimated number of days based on the estimatedNumberOfImpressions
     */
    let estimatedNumberOfDays: number = undefined;

    // Reevaluate impressions as budget gets changed
    $: estimatedNumberOfImpressions = bookingRequest.volumeTotal / (30 / 1000);

    // Reestimate number of days as estimated impressions change
    $: estimatedNumberOfDays = estimatedNumberOfImpressions / (130000 * 10);

    /**
     * For simple slots this is [slot]; for complex slots this is the list of subslots.
     * Each entry gets its own SlotAssetStep wizard page.
     */
    $: assetSteps =
        slot
            ? slot.type === SLOT_TYPES.COMPLEX && slot.slots?.length
                ? slot.slots
                : [slot]
            : [];

    /** Total number of wizard pages: one per asset step + commercial data + summary */
    $: totalPages = assetSteps.length + 2;

    /** 1-based index of the commercial data page */
    $: commercialDataPage = assetSteps.length + 1;

    /** 1-based index of the summary page */
    $: summaryPage = assetSteps.length + 2;

    function gotoNextEventListener(event: Event) {
        event.preventDefault();
        if (activePage >= totalPages) {
            bookSlotClickedHandler(event);
            return;
        }

        activePage = activePage + 1;
    }

    function gotoPreviousEventListener(event: Event) {
        event.preventDefault();
        if (activePage <= 1) {
            return;
        }
        activePage = activePage - 1;
    }

    function gotopage(page: number) {
        activePage = page;
    }

    function bookSlotClickedHandler(event: Event) {
        const selectedAssets = bookingRequest.assets.filter(
            (a: BookingAsset) => a.selected
        );
        if (selectedAssets.length == 0) {
            msgService.BroadCastError(
                "No asset selected. Please select your preferred assets",
            );
            return;
        }

        bookingRequest.assets = selectedAssets;
        campaginController
            .requestBooking(bookingRequest)
            .then((bookingRequest: Booking) => {
                msgService.BoradcastMessage(
                    "Booking request created",
                    MESSAGE_TYPE.SUCCESS,
                );
                window.location.href = "#/bookingrequest/" + bookingRequest.id;
            })
            .catch((error) => {
                msgService.BroadCastError(
                    "Could not create booking request " + error,
                );
            });

        event.preventDefault();
    }

    onMount(() => {
        let authInfo: AuthInfo = getAuthInfo();

        gamesController.getSlot(params.id).then((incomingSlot: Slot) => {
            slot = incomingSlot;

            bookingRequest.slotId = slot.id;
            bookingRequest.advertiser = authInfo.getCompanyId();
        }).catch(DefaultErrorHandler);


        let mnenomic = bip39.generateMnemonic(wordlist, 128);
        // Use only 3 words from mnenomic
        let mnenomicArr = mnenomic.split(" ");
        mnenomicArr = mnenomicArr.map(capitalizeFirstLetter);
        bookingRequest.name = mnenomicArr.slice(0, 3).join(" ");
    });

    /**
     * A callback function for the checkbox in the summary, making any and all items checked
     * @param id
     */
    function itemAllChecked(id: string): boolean {
        return true;
    }

    /**
     * Toggles the selected state of an asset in the summary step.
     * The asset stays in bookingRequest.assets; only selected changes.
     */
    function keepImageCheckboxClicked(assetId: string) {
        const asset = bookingRequest.assets.find(
            (a: BookingAsset) => a.id === assetId,
        );
        if (asset) {
            asset.selected = !asset.selected;
            bookingRequest = bookingRequest;
        }
    }
</script>

<div class="container-fluid">
    <div class="d-flex align-items-baseline justify-content-between">
        <!-- Title -->
        <h1 class="h2">Request a Booking</h1>
    </div>

    <div class="row justify-content-center">
        <div class="col-12">
            <form class="needs-validation" novalidate>
                <ul
                    class="nav nav-pills steps mb-7 mt-n3 w-75 w-xxl-50 mx-auto"
                    id="wizard-tab"
                >
                    {#each assetSteps as _step, i}
                        <li class="nav-item">
                            <button
                                class="nav-link {activePage === i + 1 ? 'active bg-success' : ''}"
                                type="button"
                                on:click={() => gotopage(i + 1)}
                            >
                                {i + 1}
                            </button>
                        </li>
                    {/each}
                    <li class="nav-item">
                        <button
                            class="nav-link {activePage === commercialDataPage ? 'active bg-success' : ''}"
                            type="button"
                            on:click={() => gotopage(commercialDataPage)}
                        >
                            {commercialDataPage}
                        </button>
                    </li>
                    <li class="nav-item">
                        <button
                            class="nav-link {activePage === summaryPage ? 'active bg-success' : ''}"
                            type="button"
                            on:click={() => gotopage(summaryPage)}
                        >
                            {summaryPage}
                        </button>
                    </li>
                </ul>

                <div class="tab-content" id="wizard-tabContent">
                    {#each assetSteps as step, i}
                        <div
                            class="tab-pane {activePage === i + 1
                                ? 'show active'
                                : 'hide'}"
                            id="wizardStepAsset{i}"
                        >
                            <SlotAssetStep slot={step} bind:bookingRequest />
                        </div>
                    {/each}
                    <div
                        class="tab-pane {activePage === commercialDataPage
                            ? 'show active'
                            : 'hide'}"
                        id="wizardStepCommercial"
                    >
                        <div class="row justify-content-center">
                            <h2 class="text-center mb-0">Commercial Data</h2>
                            <p class="text-info text-center">
                                Brand, Budget, Start Date
                            </p>

                            <!-- Card -->
                            <div class="card col-12 col-lg-6  py-6">
                                <div class="card-body">
                                    <div class="mb-3">
                                        <div
                                            class="row justify-content-center py-2"
                                        >
                                            <div class="col-6">
                                                <label
                                                    for="bookingName"
                                                    class="form-label"
                                                    >Booking Name</label
                                                >
                                            </div>
                                            <div class="col-6">
                                                <input
                                                    type="text"
                                                    class="form-control"
                                                    id="bookingName"
                                                    placeholder="Name"
                                                    bind:value={
                                                        bookingRequest.name
                                                    }
                                                />
                                                <div class="invalid-feedback">
                                                    Please add a human friendly
                                                    booking name
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            class="row justify-content-center py-2"
                                        >
                                            <div class="col-6">
                                                <label
                                                    for="brandname"
                                                    class="form-label"
                                                    >Brand</label
                                                >
                                            </div>
                                            <div class="col-6">
                                                <input
                                                    type="text"
                                                    class="form-control"
                                                    id="brandname"
                                                    placeholder="Brand Name"
                                                    bind:value={
                                                        bookingRequest.brand
                                                    }
                                                />
                                                <div class="invalid-feedback">
                                                    Please add the brand
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            class="row justify-content-center py-2"
                                        >
                                            <div class="col-6">
                                                <label
                                                    for="budget"
                                                    class="form-label"
                                                    >Budget</label
                                                >
                                            </div>
                                            <div class="col-6">
                                                <div
                                                    class="input-group input-group-merge"
                                                >
                                                    <input
                                                        type="number"
                                                        class="form-control text-end"
                                                        id="budget"
                                                        bind:value={
                                                            bookingRequest.volumeTotal
                                                        }
                                                    />
                                                    <span
                                                        class="input-group-text"
                                                        aria-describedby="budget"
                                                    >
                                                        €
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            class="row justify-content-center py-2"
                                        >
                                            <div class="col-6">
                                                <span class="form-label"
                                                    >Estimate Numer of
                                                    Impressions</span
                                                >
                                            </div>
                                            <div
                                                id="estimatedImpressions"
                                                class="col-6 justify-content-end"
                                            >
                                                {#if estimatedNumberOfImpressions}
                                                    {Math.round(
                                                        estimatedNumberOfImpressions,
                                                    ).toLocaleString()}&nbsp;<b
                                                        >impressions</b
                                                    >&nbsp;over&nbsp;{Math.round(
                                                        estimatedNumberOfDays *
                                                            100,
                                                    ) / 100}&nbsp;<b>days</b>
                                                {:else}
                                                    <BootstrapVectorIcon
                                                        style="height: 1em; width: 1em;"
                                                        icon="question"
                                                    ></BootstrapVectorIcon>
                                                {/if}
                                            </div>
                                        </div>
                                        <!-- / .row -->
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        class="tab-pane {activePage === summaryPage
                            ? 'show active'
                            : 'hide'}"
                        id="wizardStepSummary"
                    >
                        <h2 class="text-center mb-0">
                            Booking Request Summary
                        </h2>
                        <p class="text-info text-center">
                            Review, Request, Wait for Approval
                        </p>
                        <!-- Card -->
                        <BookingRequestSummary
                            showStatus={false}
                            {bookingRequest}
                            adSlot={slot}
                            {keepImageCheckboxClicked}
                            checkedItems={
                                bookingRequest.assets
                                    .filter((a: BookingAsset) => a.selected)
                                    .map((a: BookingAsset) => a.id)
                            }
                        ></BookingRequestSummary>
                    </div>
                </div>
                <div class="mt-8">
                    <div class="d-flex justify-content-center">
                        <!-- Button -->
                        <button
                            type="button"
                            on:click={gotoPreviousEventListener}
                            class="btn btn-secondary me-4"
                            >{activePage == 1 ? "Cancel" : "Back"}</button
                        >

                        <!-- Button -->
                        <button
                            type="button"
                            class="btn btn-primary ms-4"
                            data-toggle="wizard"
                            on:click={gotoNextEventListener}
                            >{activePage < totalPages ? "Next" : "Request"}</button
                        >
                    </div>
                </div>
            </form>
        </div>
    </div>
    <!-- / .row -->
</div>
<!-- / .container-fluid -->
